import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { cacheProducts, cacheEvents, setMeta, getMeta, clearAll } from "./db";
import { processQueue, queueSize } from "./sync";

const OnlineContext = createContext({ online: true, lastSync: null, syncNow: () => {}, syncing: false, queued: 0 });

/**
 * Network status + bootstrap-cache provider.
 * Fetches /api/sync/bootstrap when authenticated + online and stores results in IndexedDB.
 */
export function OnlineProvider({ children, authedUserId }) {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [queued, setQueued] = useState(0);

  const refreshQueueCount = useCallback(async () => {
    try { setQueued(await queueSize()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshQueueCount();
    const t = setInterval(refreshQueueCount, 4000);
    return () => clearInterval(t);
  }, [refreshQueueCount]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Hydrate lastSync from IDB on mount
  useEffect(() => {
    getMeta("lastSync").then((v) => v && setLastSync(v)).catch(() => {});
  }, []);

  const syncNow = useCallback(async () => {
    if (!authedUserId) return;
    setSyncing(true);
    try {
      // 1. Drain the local mutation queue first so server has the latest state
      await processQueue();
      // 2. Re-pull bootstrap to refresh local cache
      const { data } = await api.get("/sync/bootstrap");
      await cacheProducts(data.products || []);
      await cacheEvents(data.events || []);
      const ts = new Date().toISOString();
      await setMeta("lastSync", ts);
      await setMeta("user_id", data.user?.user_id || "");
      if (data.user) {
        await setMeta("user", data.user);
        // also mirror to localStorage so AuthProvider can hydrate synchronously on next reload
        try { localStorage.setItem("vv_user", JSON.stringify(data.user)); } catch (_) { /* ignore */ }
      }
      setLastSync(ts);
    } catch (e) {
      // soft-fail; we keep whatever's cached
    } finally {
      setSyncing(false);
      refreshQueueCount();
    }
  }, [authedUserId, refreshQueueCount]);

  // Auto-sync on auth + when transitioning offline → online
  useEffect(() => {
    if (online && authedUserId) syncNow();
  }, [online, authedUserId, syncNow]);

  // If user changed (logout / different account) wipe local cache
  useEffect(() => {
    (async () => {
      const cachedUid = await getMeta("user_id").catch(() => null);
      if (authedUserId && cachedUid && cachedUid !== authedUserId) {
        await clearAll();
        await setMeta("user_id", authedUserId);
        if (online) syncNow();
      }
    })();
  }, [authedUserId, online, syncNow]);

  return (
    <OnlineContext.Provider value={{ online, lastSync, syncNow, syncing, queued, refreshQueueCount }}>
      {children}
    </OnlineContext.Provider>
  );
}

export const useOnline = () => useContext(OnlineContext);
